import { ICouponRepository } from '../interfaces';
import LogUtil from '../../../core/utils/LogUtil';

/**
 * Coupon Repository Implementation
 * 쿠폰 관리 API 호출 구현체
 */
export class CouponRepositoryImpl extends ICouponRepository {
  constructor(apiClient) {
    super();
    this.apiClient = apiClient;
    LogUtil.log('🔧 [CouponRepository] 초기화 완료');
  }

  /**
   * 쿠폰 생성
   * POST /coupon/create
   */
  async createCoupon(data) {
    try {
      LogUtil.log('📤 [CouponRepository] 쿠폰 생성 요청:', data);
      const response = await this.apiClient.post('/coupon/create', data);
      LogUtil.log('📥 [CouponRepository] 쿠폰 생성 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 쿠폰 생성 실패:', error);
      throw new Error(`쿠폰 생성 실패: ${error.message}`);
    }
  }

  /**
   * 쿠폰 목록 조회
   * GET /coupon/list
   */
  async getCoupons(params = {}) {
    try {
      LogUtil.log('📤 [CouponRepository] 쿠폰 목록 조회 요청:', params);
      const response = await this.apiClient.get('/coupon/list', { params });
      LogUtil.log('📥 [CouponRepository] 쿠폰 목록 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 쿠폰 목록 조회 실패:', error);
      throw new Error(`쿠폰 목록 조회 실패: ${error.message}`);
    }
  }

  /**
   * 쿠폰 상세 조회
   * GET /coupon/{coupon_id}
   */
  async getCouponById(couponId) {
    try {
      LogUtil.log('📤 [CouponRepository] 쿠폰 상세 조회 요청:', couponId);
      const response = await this.apiClient.get(`/coupon/${couponId}`);
      LogUtil.log('📥 [CouponRepository] 쿠폰 상세 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 쿠폰 상세 조회 실패:', error);
      throw new Error(`쿠폰 상세 조회 실패: ${error.message}`);
    }
  }

  /**
   * 사용자별 쿠폰 목록 조회
   * GET /coupon/user/{user_id}
   */
  async getCouponsByUserId(userId, params = {}) {
    try {
      LogUtil.log('📤 [CouponRepository] 사용자별 쿠폰 조회 요청:', { userId, params });
      const response = await this.apiClient.get(`/coupon/user/${userId}`, { params });
      LogUtil.log('📥 [CouponRepository] 사용자별 쿠폰 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 사용자별 쿠폰 조회 실패:', error);
      throw new Error(`사용자별 쿠폰 조회 실패: ${error.message}`);
    }
  }

  /**
   * 타겟팅별 대상자 수 조회
   * GET /coupon/eligible-count/{pharmacyId}
   */
  async getEligibleCount(pharmacyId, params = {}) {
    try {
      LogUtil.log('📤 [CouponRepository] 대상자 수 조회 요청:', { pharmacyId, params });
      const response = await this.apiClient.get(`/coupon/eligible-count/${pharmacyId}`, { params });
      LogUtil.log('📥 [CouponRepository] 대상자 수 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 대상자 수 조회 실패:', error);
      throw new Error(`대상자 수 조회 실패: ${error.message}`);
    }
  }

  /**
   * 약국별 쿠폰 발행/사용 내역 조회
   * GET /coupon/pharmacy-history/{pharmacyId}
   */
  async getPharmacyHistory(pharmacyId, params = {}) {
    try {
      LogUtil.log('📤 [CouponRepository] 약국 쿠폰 내역 조회 요청:', { pharmacyId, params });
      const response = await this.apiClient.get(`/coupon/pharmacy-history/${pharmacyId}`, { params });
      LogUtil.log('📥 [CouponRepository] 약국 쿠폰 내역 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [CouponRepository] 약국 쿠폰 내역 조회 실패:', error);
      throw new Error(`약국 쿠폰 내역 조회 실패: ${error.message}`);
    }
  }

  // ===== 이벤트 만들기: 템플릿 · 질병 · 타겟 그룹 (약국 토큰 필수, apiClient 가 자동 첨부) =====

  async _call(label, request) {
    try {
      const response = await request();
      LogUtil.log(`📥 [CouponRepository] ${label} 응답:`, response);
      return response;
    } catch (error) {
      LogUtil.error(`❌ [CouponRepository] ${label} 실패:`, error);
      throw new Error(`${label} 실패: ${error.message}`);
    }
  }

  /** GET /coupon/templates → { list: [{id,name,target_group_id,product_ids,discount_type,discount_value,default_period_days,guide_text,created_at}] } */
  getTemplates() {
    return this._call('쿠폰 템플릿 조회', () => this.apiClient.get('/coupon/templates'));
  }

  /** POST /coupon/templates */
  createTemplate(data) {
    return this._call('쿠폰 템플릿 저장', () => this.apiClient.post('/coupon/templates', data));
  }

  /** DELETE /coupon/templates/{id} */
  deleteTemplate(templateId) {
    return this._call('쿠폰 템플릿 삭제', () => this.apiClient.delete(`/coupon/templates/${templateId}`));
  }

  /** GET /coupon/conditions → { data: { groups: [{label, items: [{code,name,patient_count}]}] } } */
  getConditions() {
    return this._call('처방 질병 조회', () => this.apiClient.get('/coupon/conditions'));
  }

  /** GET /coupon/target-groups/recommended → { list: [{key,name,detail,condition:{last_visit_days_gte|target_conditions},count}] } */
  getRecommendedTargetGroups() {
    return this._call('추천 타겟 조회', () => this.apiClient.get('/coupon/target-groups/recommended'));
  }

  /** GET /coupon/target-groups → { list: [{id,name,detail,target_gender,target_ages,target_conditions,last_visit_days_gte,count,created_at}] } */
  getTargetGroups() {
    return this._call('타겟 그룹 조회', () => this.apiClient.get('/coupon/target-groups'));
  }

  /** POST /coupon/target-groups */
  createTargetGroup(data) {
    return this._call('타겟 그룹 저장', () => this.apiClient.post('/coupon/target-groups', data));
  }

  /** PUT /coupon/target-groups/{id} */
  updateTargetGroup(groupId, data) {
    return this._call('타겟 그룹 수정', () => this.apiClient.put(`/coupon/target-groups/${groupId}`, data));
  }

  /** DELETE /coupon/target-groups/{id} */
  deleteTargetGroup(groupId) {
    return this._call('타겟 그룹 삭제', () => this.apiClient.delete(`/coupon/target-groups/${groupId}`));
  }
}
